import update from 'immutability-helper';
import uuid from 'uuid-random';
import * as Types from './types';
import { createReducer } from '../../utils';
import { isServiceError } from '../../utils/errorCodes';

const MultimediaCollection = Object.freeze({
  IMAGE: 'pictures',
  VIDEO: 'videos',
  VIDEO360: 'tour360',
  PLAN: 'plan'
});

const MultimediaType = Object.freeze({
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  TOUR360: 'TOUR360',
  PLAN: 'PLAN'
});

function normalize(data) {
  const result = {
    pictures: [],
    videos: [],
    plan: [],
    tour360: []
  };
  Object.values(MultimediaCollection).forEach((propertyName) => {
    // Verificamos si existen datos para este tipo de multimedia
    if (data[propertyName]) {
      // Transformamos cada elemento: añadimos ID único, inicializamos campo error
      result[propertyName] = data[propertyName]
        .map((item) => ({
          ...item,
          id: uuid(),
          error: null
        }))
        .sort((a, b) => a.order - b.order);
    }
  });

  return result;
}

function deleteMultimedia(id, collection) {
  // Encontramos el índice del elemento a eliminar
  const index = collection.findIndex((m) => m.id === id);

  // Si no encontramos el elemento, devolvemos la colección sin cambios
  if (index === -1) return collection;

  const multimedia = collection[index];
  // Si el elemento tiene id_multimedia, lo marcamos como eliminado de lo contrario lo eliminamos completamente
  const updateOperation = multimedia.id_multimedia
    ? { $splice: [[index, 1, { ...multimedia, delete: true }]] }
    : { $splice: [[index, 1]] };
  // Actualizamos la collection
  const updatedCollection = update(collection, updateOperation);

  // Actualizamos los órdenes de los elementos posteriores al eliminado
  return updatedCollection.map((item, i) => {
    if (i > index) {
      // Si el orden actual ya coincide con la nueva posición, no marcamos como updated
      const newOrder = i + 1;
      const needsUpdate = newOrder !== item.order;

      return {
        ...item,
        order: newOrder,
        // Sólo marcamos como updated si realmente cambió el orden
        ...(needsUpdate && { updated: true })
      };
    }
    return item;
  });
}

function addErrorMultimedia(id, collection, keyError) {
  const index = collection.findIndex((m) => m.id === id);
  const multimedia = collection[index];

  return update(collection, {
    $splice: [[index, 1, { ...multimedia, error: multimedia.error || keyError }]]
  });
}

function cleanMultimedia(collection) {
  const multimedia = collection.filter((p) => !p.error && (p.temporalUrl || p.url));

  return update(collection, {
    $set: multimedia
  });
}

function updateMultimedia(updatedMultimedia, collection) {
  const index = collection.findIndex((m) => m.id === updatedMultimedia.id);
  const multimedia = collection[index];
  return update(collection, {
    $splice: [[index, 1, { ...multimedia, ...updatedMultimedia, updated: true }]]
  });
}

function moveMultimedia(dragIndex, hoverIndex, collection) {
  // Mapeamos índices visibles (sin delete) a índices reales del array completo
  const visibleItems = collection
    // Mapeamo primero antes de filtrar para mantener los índices reales de los elementos que se van a mover
    .map((item, realIndex) => ({ realIndex, item }))
    .filter(({ item }) => !item.delete);

  // Verificamos índices válidos
  if (dragIndex < 0 || hoverIndex < 0 || dragIndex >= collection.length || hoverIndex >= collection.length) {
    return collection;
  }

  // Convertimos índices visibles a índices reales y movemos el elemento
  const realDragIndex = visibleItems[dragIndex].realIndex;
  const realHoverIndex = visibleItems[hoverIndex].realIndex;
  const reorderedCollection = [...collection];
  // Realizamos la operación de mover (quitar de dragIndex e insertar en hoverIndex)
  const [draggedItem] = reorderedCollection.splice(realDragIndex, 1);
  reorderedCollection.splice(realHoverIndex, 0, draggedItem);

  // Actualizamos los órdenes de los elementos visibles
  let visibleOrder = 0;
  return reorderedCollection.map((item) => {
    if (item.delete) return item;

    visibleOrder += 1;
    return visibleOrder === item.order ? item : { ...item, order: visibleOrder, updated: true };
  });
}

function addMultimedia(multimedia, collection) {
  const isVideo = multimedia.multimediaType === 'VIDEO';
  const order = collection.length === 0 || isVideo ? 1 : collection.length + 1;
  // Formateamos el elemento con todas las propiedades requeridas:
  const formatted = {
    ...multimedia,
    id: multimedia.id || uuid(),
    delete: false,
    tag360: false,
    title: null,
    id_multimedia: null,
    order,
    error: multimedia.error || null
  };

  if (isVideo) {
    // Primero incrementamos el orden de todos los elementos existentes
    const reorderedCollection = collection.map((item) => ({
      ...item,
      order: item.order + 1,
      updated: true
    }));

    // Añadimos el nuevo vídeo al principio
    return update(reorderedCollection, {
      $unshift: [formatted]
    });
  }

  // Para otros tipos, simplemente añadimos al final
  return update(collection, {
    $push: [formatted]
  });
}

const initialMultimediaState = {
  multimedias: {
    videos: [],
    pictures: [],
    tour360: [],
    plan: []
  },
  originalMultimedia: null,
  getMultimediaLoading: false,
  getMultimediaError: null,
  getMultimediaCompleted: false,
  // Generación del preview de las imagenes y planos
  previewStorageLoading: false,
  previewStorageItems: {},
  previewStorageError: false,
  // Validación del 360
  validation360Loading: false,
  validationVideo360Success: false,
  validationVideo360Error: false,
  validatedVideo360: false
};

const multimediaStepReducer = createReducer(initialMultimediaState)({
  [Types.GET_STEP_MULTIMEDIA]: (state) => ({
    ...state,
    getMultimediaError: null,
    getMultimediaLoading: true,
    getMultimediaCompleted: false
  }),
  [`${Types.GET_STEP_MULTIMEDIA}_COMPLETED`]: (state, action) => {
    const normalizedData = normalize(action.payload);
    return {
      ...state,
      getMultimediaLoading: false,
      getMultimediaCompleted: true,
      multimedias: normalizedData,
      originalMultimedia: normalizedData
    };
  },
  [`${Types.GET_STEP_MULTIMEDIA}_FAILED`]: (state, action) => ({
    ...state,
    getMultimediaCompleted: true,
    getMultimediaLoading: false,
    getMultimediaError: isServiceError(action?.payload?.status)
  }),

  [Types.PREVIEW_STORAGE_IMAGE]: (state, action) => ({
    ...state,
    previewStorageItems: {
      ...state.previewStorageItems,
      [action.meta.itemId]: {
        previewStorageLoading: true,
        response: null,
        previewStorageError: false
      }
    }
  }),
  [`${Types.PREVIEW_STORAGE_IMAGE}_COMPLETED`]: (state, action) => ({
    ...state,
    previewStorageItems: {
      ...state.previewStorageItems,
      [action.meta.itemId]: {
        previewStorageLoading: false,
        response: action.payload,
        previewStorageError: false
      }
    }
  }),
  [`${Types.PREVIEW_STORAGE_IMAGE}_FAILED`]: (state, action) => ({
    ...state,
    previewStorageItems: {
      ...state.previewStorageItems,
      [action.meta.itemId]: {
        previewStorageLoading: false,
        response: action.payload,
        previewStorageError: isServiceError(action?.payload?.status)
      }
    }
  }),

  [Types.RESET_PREVIEW_STORAGE]: (state, action) => {
    const { itemId } = action.payload || {};
    if (itemId) {
      const updatedItems = { ...state.previewStorageItems };
      delete updatedItems[itemId];
      return {
        ...state,
        previewStorageItems: updatedItems
      };
    }
  },

  [Types.VALIDATE_VIDEO_360]: (state) => ({
    ...state,
    validationVideo360Error: false,
    validation360Loading: true,
    validatedVideo360: false
  }),
  [`${Types.VALIDATE_VIDEO_360}_COMPLETED`]: (state, action) => ({
    ...state,
    validation360Loading: false,
    validationVideo360Success: action.payload.status === 200,
    validatedVideo360: true
  }),
  [`${Types.VALIDATE_VIDEO_360}_FAILED`]: (state, action) => ({
    ...state,
    validation360Loading: false,
    validationVideo360Error: isServiceError(action?.payload?.status),
    validatedVideo360: true
  }),

  [Types.ADD_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: addMultimedia(action.payload.multimedia, state.multimedias[action.payload.type])
    }
  }),
  [Types.MOVE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: moveMultimedia(
        action.payload.dragIndex,
        action.payload.hoverIndex,
        state.multimedias[action.payload.type]
      )
    }
  }),
  [Types.UPDATE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: updateMultimedia(action.payload.multimedia, state.multimedias[action.payload.type])
    }
  }),
  [Types.DELETE_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: deleteMultimedia(action.payload.id, state.multimedias[action.payload.type])
    }
  }),
  [Types.ADD_ERROR_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: addErrorMultimedia(
        action.payload.id,
        state.multimedias[action.payload.type],
        action.payload.keyError
      )
    }
  }),
  [Types.CLEAN_MULTIMEDIA]: (state, action) => ({
    ...state,
    multimedias: {
      ...state.multimedias,
      [action.payload.type]: cleanMultimedia(state.multimedias[action.payload.type])
    }
  }),
  [Types.RESET_INFO]: () => initialMultimediaState,

  [Types.CLEAN_MULTIMEDIA_GET]: (state) => ({
    ...state,
    getMultimediaCompleted: false
  })
});

export { multimediaStepReducer, MultimediaType, MultimediaCollection };
